/**
 * CeoCharts — the three graphics Carlos asked for (Card P196).
 *
 * Revenue over time, the pipeline-movement waterfall, and the sales funnel.
 * All three read ONE payload from reports:ceo:charts, so nothing here can
 * disagree with anything else: every number on screen arrived from a query.
 *
 * ⛔⛔ "REVENUE", NEVER "ARR". STEP 0 measured it — crm_subscriptions has no
 * working writer and ghl_invoices has no renewal model, so there is no
 * recurring revenue to annualise. Charting ARR here would be inventing a
 * business model from a chart title.
 *
 * ⚠ recharts, already a dependency and already used by the Overview's trend
 * card. No new charting library.
 *
 * ⛔⛔ isAnimationActive={false} ON EVERY SERIES, AND IT IS NOT A PREFERENCE.
 * MEASURED 31 Oct 2026 (card P201) with --print-to-pdf: recharts animates a
 * bar from zero width on mount, and a print pipeline captures the page before
 * that animation finishes. The PDF came back with every axis, every tick and
 * every label correct and NO BARS — one 6px sliver where a $690,000 bar
 * should have been. ⚠⚠ NOTHING IN jsdom CAN SEE THIS: recharts does not lay
 * out there at all, so the suite was green over a chart that printed blank,
 * and so was the screen, because on screen the animation does finish. The
 * only control that catches it is a screenshot, which is why P201 attaches
 * one. Turning the animation off also makes the charts deterministic for any
 * future screenshot diff.
 *
 * ⚠ Dark/light: every colour comes from the shared theme tokens, and nothing
 * relies on a fill alone to carry meaning — each bar is labelled, because a
 * verdict encoded only in colour is unreadable in the other theme and
 * untestable in either.
 */

import React, { useMemo } from 'react';
import {
  ResponsiveContainer, ComposedChart, Area, Line, BarChart, Bar, Cell,
  PieChart, Pie, Legend,
  XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine, LabelList,
} from 'recharts';
import { C, FONT, SERIES_OTHER, SERIES_OTHER_INK } from './_overviewTheme';
import {
  barChartHeight, truncateLabel, barEndLabel, topNWithRemainder, foldCaption,
  labelFits, labelFitsMargin, axisWidthFor, TOP_N,
} from './_chartSizing';

const money0 = (n) => `$${Math.round(Number(n) || 0).toLocaleString()}`;
const centsToDollars = (c) => (Number(c) || 0) / 100;

/**
 * ⛔⛔ THE LOGIC LIVES OUTSIDE THE CHART, DELIBERATELY.
 *
 * recharts does not lay out in jsdom — it renders no axis tick labels and no
 * sized bars — so an assertion against the rendered SVG is unreliable in BOTH
 * directions: it fails on correct code, and it would pass over a chart that
 * draws nothing. (A screenshot is the only honest test of the drawing itself;
 * this repo has been bitten by a print stylesheet that parsed to zero rules
 * while every jsdom test stayed green.)
 *
 * So the two rules that actually matter — funnel ORDER and waterfall
 * ARITHMETIC — are pure functions, exported and tested directly. The
 * components are then thin enough that rendering them is a smoke test.
 */

/**
 * The funnel LADDER: progression buckets only, in sort_order, with
 * stage-to-stage conversion.
 * ⚠ Terminal side-states are removed BY NAME, never by position —
 * routes/_funnel_report.js records why: sort_order alone files Lost and
 * Nurture as late funnel stages, making the funnel look like it ends in
 * failure.
 */
export function funnelLadder(funnel, terminalBuckets = []) {
  const d = (funnel && funnel.tracked && funnel.data) || [];
  const ladder = d.filter((r) => !terminalBuckets.includes(r.bucket));
  return ladder.map((r, i) => {
    const prev = i > 0 ? Number(ladder[i - 1].deals) || 0 : null;
    const deals = Number(r.deals) || 0;
    return {
      bucket: r.bucket,
      deals,
      value: Number(r.value) || 0,
      conversion: prev ? Math.round((deals / prev) * 1000) / 10 : null,
    };
  });
}

/**
 * The waterfall staircase. Each step spans [runningBefore, runningAfter], so
 * the bars ARE the arithmetic rather than a drawing of it — if they did not
 * reconcile, the last bar would not land on `end`, visibly.
 */
export function waterfallSteps(movement) {
  const m = (movement && movement.tracked && movement.data) || null;
  if (!m) return [];
  const num = (v) => Number(v) || 0;
  const parts = [
    /*
     * ⚠ TWO LABELS PER STEP — Card P208. Six full labels on a ~354px card ran
     * together into "New dealsMoved in Won LostMoved out". `short` is what the
     * axis draws; `label` is what the tooltip and the drill-down say, so the
     * abbreviation never becomes the only name the figure has.
     */
    { key: 'start', label: 'Open at start', short: 'Start', delta: num(m.start_value), kind: 'total', n: m.start_n },
    { key: 'new', label: 'New deals', short: 'New', delta: num(m.new_value), kind: 'up', n: m.new_n },
    { key: 'movedIn', label: 'Moved in', short: 'In', delta: num(m.moved_in_value), kind: 'up', n: m.moved_in_n },
    { key: 'won', label: 'Won', short: 'Won', delta: -num(m.won_value), kind: 'down', n: m.won_n },
    { key: 'lost', label: 'Lost', short: 'Lost', delta: -num(m.lost_value), kind: 'down', n: m.lost_n },
    { key: 'movedOut', label: 'Moved out', short: 'Out', delta: -num(m.moved_out_value), kind: 'down', n: m.moved_out_n },
  ];
  let running = 0;
  const out = parts.map((p) => {
    const before = p.kind === 'total' ? 0 : running;
    running = p.kind === 'total' ? p.delta : running + p.delta;
    return { ...p, running, span: [Math.min(before, running), Math.max(before, running)] };
  });
  out.push({
    key: 'end', label: 'Open at end', short: 'End', delta: num(m.end_value), kind: 'total',
    n: m.end_n, running: num(m.end_value), span: [0, num(m.end_value)],
  });
  return out;
}

/**
 * A card: title, optional caveat, a FIXED-HEIGHT chart box, and an optional
 * footer BELOW that box.
 *
 * ⛔⛔ WHY `footer` EXISTS — Card P208.
 *
 * The funnel's "⚠ N stage(s) have no funnel mapping" note ran off the bottom
 * of its card on a 1413px desktop and vanished behind the KPI tiles. The cause
 * was not CSS: SalesFunnel passed the note as a SECOND CHILD of Panel, and
 * Panel rendered `{children}` INSIDE the ResponsiveContainer, inside the
 * fixed-height chart div. ResponsiveContainer expects one chart child and
 * absolutely positions what it is given, so the note was laid out inside a
 * 220px box that the chart already filled.
 *
 * ⚠ So the note was never "in the card" in the sense that matters. A footer
 * slot rendered AFTER the chart box is the fix; `height` now applies ONLY to
 * that box, and the CARD's height follows its content.
 */
function Panel({ title, caveat, height = 200, children, footer, testid }) {
  return (
    <section
      data-testid={testid}
      data-print-section
      style={{
        background: C.card, border: `1px solid ${C.border}`, borderRadius: 8,
        padding: 12, fontFamily: FONT, minWidth: 0,
        /* ⚠ The card grows with its content. No fixed height anywhere on it. */
        height: 'auto',
        display: 'flex', flexDirection: 'column',
        /* ⛔ A long stage name must wrap rather than widen the card and push
            the grid into a horizontal scroll. */
        overflowWrap: 'anywhere', wordBreak: 'break-word',
      }}
    >
      <h3 style={{ margin: '0 0 2px', fontSize: 13, fontWeight: 700, color: C.text }}>{title}</h3>
      {caveat ? (
        <div style={{ fontSize: 11, color: C.muted, marginBottom: 6, lineHeight: 1.4 }}>{caveat}</div>
      ) : null}
      {/* ⚠ minWidth 0 + a responsive container: on a phone the chart shrinks
          rather than pushing the page into a horizontal scroll.
          ⛔ flexShrink 0 so the chart keeps its height and the FOOTER is what
          makes the card taller — never the chart being squeezed. */}
      {/*
        * ⭐ Card P222 item 1 / P242 — THE PLOT SITS ON WHITE. These panels
        * carry their own card chrome, so wrapping them in B1's ChartWell
        * would nest a grey box inside a white one. The well belongs HERE, on
        * the plot area itself: one white ground, whichever surface hosts it,
        * and the outlines below are measured against this colour.
        */}
      <div data-testid={testid ? `${testid}-plot` : undefined}
           style={{
             height, minWidth: 0, flexShrink: 0,
             background: C.well, borderRadius: 6,
             border: `1px solid ${C.border}`, padding: 4, boxSizing: 'border-box',
           }}>
        <ResponsiveContainer width="100%" height="100%">{children}</ResponsiveContainer>
      </div>
      {footer || null}
    </section>
  );
}

/**
 * Compact money for AXIS TICKS — $1.5M, $820k, $0.
 *
 * ⛔ Card P208: the revenue Y axis rendered "‚000,000" because a full
 * `$2,400,000` was wider than the 64px axis gutter and recharts CLIPS rather
 * than widens. A clipped number is not a smaller number, it is a WRONG one —
 * the reader sees leading digits missing with no indication.
 *
 * ⚠ Ticks only. Tooltips and labels keep the exact figure; rounding is
 * acceptable on an axis and is not acceptable on a value someone might quote.
 */
const moneyTick = (n) => {
  const v = Number(n) || 0;
  const a = Math.abs(v);
  const sign = v < 0 ? '-' : '';
  if (a >= 1e9) return `${sign}$${Math.round(a / 1e8) / 10}B`;
  if (a >= 1e6) return `${sign}$${Math.round(a / 1e5) / 10}M`;
  if (a >= 1e3) return `${sign}$${Math.round(a / 1e2) / 10}k`;
  return `${sign}$${Math.round(a)}`;
};

/** Axis gutter wide enough for the widest tick this formatter can produce. */
const MONEY_AXIS_WIDTH = 56;

/** ── 1 · Revenue over time ─────────────────────────────────────────────── */
export function RevenueChart({ data, targets, label = 'Revenue', height = 200 }) {
  const series = useMemo(() => (data || []).map((r) => ({
    month: r.month,
    revenue: centsToDollars(r.collected_cents),
    target: targets && targets[r.month] != null ? Number(targets[r.month]) : null,
  })), [data, targets]);

  const hasTarget = series.some((p) => p.target != null);

  return (
    <Panel
      testid="chart-revenue"
      title={`${label} over time`}
      caveat={hasTarget
        ? 'Dashed line is the configured target.'
        /* ⛔ No invented target. An unset target draws nothing and says so. */
        : 'No target configured, so none is drawn.'}
      height={height}
    >
      <ComposedChart data={series} margin={{ top: 6, right: 10, left: 0, bottom: 0 }}>
        <CartesianGrid stroke={C.borderSoft} vertical={false} />
        {/* ⚠ Card P208: 12 months on a ~354px card — every other tick, so the
            labels do not run together the way the waterfall's did. */}
        <XAxis dataKey="month" tick={{ fontSize: 11, fill: C.faint }} axisLine={false}
               tickLine={false} minTickGap={8} />
        {/* ⛔ Card P208: this rendered "‚000,000" — a full $2,400,000 is wider
            than the gutter and recharts CLIPS rather than widens, so the
            reader saw a number with its leading digits missing. */}
        <YAxis tick={{ fontSize: 11, fill: C.faint }} axisLine={false} tickLine={false}
               width={MONEY_AXIS_WIDTH} tickFormatter={moneyTick} />
        <Tooltip formatter={(v) => money0(v)} />
        <Area isAnimationActive={false} type="monotone" dataKey="revenue" stroke={C.primary} fill={C.primary}
              fillOpacity={0.12} strokeWidth={2} name={label} />
        {hasTarget ? (
          <Line isAnimationActive={false} type="monotone" dataKey="target" stroke={C.watch} strokeWidth={2}
                strokeDasharray="5 4" dot={false} name="Target" />
        ) : null}
      </ComposedChart>
    </Panel>
  );
}

/**
 * ── 2 · Pipeline movement waterfall ────────────────────────────────────────
 * ⚠ Rendered as floating bars: each step's bar spans [runningBefore, runningAfter],
 * so the staircase is the arithmetic rather than a drawing of it. If the bars
 * did not reconcile the last bar would not land on `end`, visibly.
 */
export function MovementWaterfall({ movement, basis, onDrill, height = 220 }) {
  const steps = useMemo(() => waterfallSteps(movement), [movement]);

  if (!movement || !movement.tracked) {
    return (
      <Panel testid="chart-waterfall" title="Pipeline movement" height={height}>
        <BarChart data={[]} />
      </Panel>
    );
  }

  const assumed = movement.data && Number(movement.data.assumed_static) || 0;

  return (
    <Panel
      testid="chart-waterfall"
      title="Pipeline movement"
      caveat={
        /*
         * ⛔⛔ SAID ON SCREEN, not buried in a comment. Opportunity value is not
         * journalled, so every bar attributes the deal's CURRENT value to the
         * movement it made. A reader who does not know that will over-trust it.
         */
        `Bars use each deal's current value — opportunity value is not journalled, `
        + `so historical values cannot be reconstructed.`
        + (assumed ? ` ${assumed} deal(s) have no stage history and are assumed unchanged.` : '')
      }
      height={height}
    >
      <BarChart data={steps} margin={{ top: 6, right: 10, left: 0, bottom: 0 }}>
        <CartesianGrid stroke={C.borderSoft} vertical={false} />
        <XAxis dataKey="short" tick={{ fontSize: 10, fill: C.faint }} axisLine={false}
               tickLine={false} interval={0} />
        <YAxis tick={{ fontSize: 11, fill: C.faint }} axisLine={false} tickLine={false}
               width={MONEY_AXIS_WIDTH} tickFormatter={moneyTick} />
        {/* ⚠ The tooltip restores the FULL label and the exact figure — the
            axis is abbreviated, the number a reader quotes is not. */}
        <Tooltip
          labelFormatter={(_l, payload) => (payload && payload[0] && payload[0].payload.label) || ''}
          formatter={(v) => (Array.isArray(v) ? money0(v[1] - v[0]) : money0(v))} />
        <ReferenceLine y={0} stroke={C.rule} />
        <Bar isAnimationActive={false} dataKey="span" radius={[2, 2, 2, 2]}
             onClick={(d) => onDrill && d && onDrill(d.key, d.label)}
             cursor={onDrill ? 'pointer' : 'default'}>
          {steps.map((s) => (
            <Cell key={s.key}
                  fill={s.kind === 'total' ? C.primaryDeep : s.kind === 'up' ? C.primary : C.high} />
          ))}
          {/* ⚠ Every bar is LABELLED with its count. Colour alone would be
              unreadable in the other theme and untestable in either. */}
          <LabelList dataKey="n" position="top" style={{ fontSize: 10, fill: C.muted }} />
        </Bar>
      </BarChart>
    </Panel>
  );
}

/** ── 3 · Sales funnel ──────────────────────────────────────────────────── */
export function SalesFunnel({ funnel, unmappedStages, winRate, terminalBuckets = [],
                              onDrill, height = null, compact = false }) {
  const rows = useMemo(() => funnelLadder(funnel, terminalBuckets), [funnel, terminalBuckets]);
  const [showUnmapped, setShowUnmapped] = React.useState(false);
  const axisWidth = axisWidthFor(rows.map((r) => r.bucket),
    { min: compact ? 96 : 130, max: compact ? 150 : 190 });
  /*
   * ⛔ Card P227 A — the third vertical-layout chart, exposed the same way: a
   * funnel bucket is a stage name too. Fixed at 220 it was one long bucket
   * list away from the overlap Daniel printed.
   */
  const h = height || barChartHeight(rows.map((r) => r.bucket), axisWidth, { min: 180 });

  const wr = winRate && winRate.tracked && winRate.data;
  const won = wr ? Number(wr.won) || 0 : 0;
  const lost = wr ? Number(wr.lost) || 0 : 0;
  const winPct = wr && (won + lost) > 0 ? Math.round((won / (won + lost)) * 1000) / 10 : null;
  const unmapped = (unmappedStages && unmappedStages.tracked && unmappedStages.data) || [];

  /*
   * ⛔ Unmapped stages are LISTED, never dropped — they are in the totals and
   * cannot be placed in the funnel, and a reader must know that.
   *
   * ⚠ Card P208: this is a FOOTER, not a chart child. Passed as a child it
   * landed inside the ResponsiveContainer's fixed-height box and ran off the
   * bottom of the card. It also COLLAPSES by default: nine stage names on a
   * ~354px card is several lines of text above the fold of everything else.
   */
  const footer = !compact && unmapped.length ? (
    <div data-testid="funnel-unmapped"
         style={{ marginTop: 8, fontSize: 11, color: C.watchDeep, lineHeight: 1.5,
                  overflowWrap: 'anywhere' }}>
      ⚠ {unmapped.length} stage(s) have no funnel mapping and are not placed above.{' '}
      <button type="button" data-testid="funnel-unmapped-toggle"
              aria-expanded={showUnmapped}
              onClick={() => setShowUnmapped((v) => !v)}
              style={{ background: 'none', border: 'none', padding: 0, font: 'inherit',
                       color: C.primaryDeep, textDecoration: 'underline', cursor: 'pointer' }}>
        {showUnmapped ? 'hide' : 'show'}
      </button>
      {showUnmapped ? (
        <div data-testid="funnel-unmapped-list" style={{ marginTop: 4 }}>
          {unmapped.map((u) => `${u.stage} (${u.deals})`).join(', ')}
        </div>
      ) : null}
    </div>
  ) : null;

  return (
    <Panel
      testid={compact ? 'chart-funnel-compact' : 'chart-funnel'}
      title="Sales funnel"
      caveat={
        /*
         * ⚠ Card P207: the funnel is CUMULATIVE — each bar is "reached at
         * least this stage", so a step can never exceed 100%. Said on screen,
         * because a reader who assumes occupancy will misread every bar.
         */
        (winPct == null
          ? 'Win rate is not tracked for this window. '
          : `Win rate ${winPct}% (${won} won / ${lost} lost) in this window. `)
        + 'Bars count deals that reached at least that stage.'
      }
      height={h}
      footer={footer}
    >
      <BarChart data={rows} layout="vertical" margin={{ top: 4, right: 52, left: 0, bottom: 0 }}>
        <CartesianGrid stroke={C.borderSoft} horizontal={false} />
        {/* ⚠ Card P208: headroom so the right-hand % label sits INSIDE the
            card at the widest value rather than being clipped by its edge. */}
        <XAxis type="number" tick={{ fontSize: 11, fill: C.faint }} axisLine={false}
               tickLine={false} domain={[0, (max) => Math.ceil((max || 1) * 1.15)]} />
        <YAxis type="category" dataKey="bucket" width={axisWidth}
               tick={{ fontSize: 11, fill: C.body }} axisLine={false} tickLine={false} />
        <Tooltip formatter={(v, k) => (k === 'value' ? money0(v) : v)} />
        <Bar isAnimationActive={false} dataKey="deals" fill={C.primary} radius={[0, 2, 2, 0]}
             onClick={(d) => onDrill && d && onDrill(d.bucket)}
             cursor={onDrill ? 'pointer' : 'default'}>
          <LabelList
            dataKey="conversion" position="right"
            formatter={(v) => (v == null ? '' : `${v}%`)}
            style={{ fontSize: 10, fill: C.muted }}
          />
        </Bar>
      </BarChart>
    </Panel>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
 * Card P201 — four more charts, for the CEO AI report's chart allowlist.
 *
 * ⛔ ADDED HERE, NOT IN A NEW FILE. The report needs funnel / waterfall /
 * trend, which already live above, plus these four. A second chart module
 * would have meant a second funnel, a second theme import and a second set of
 * axis conventions — the shape this repo keeps paying for. Same Panel, same C
 * tokens, same "label every bar" rule.
 *
 * ⚠ NO HARD-CODED HEXES. Every colour below resolves from _overviewTheme, so
 * these inherit the soft-UI retune and the AA-corrected tokens automatically.
 * The one exception is GREY FOR UNMAPPED, which is C.muted — a token, used
 * deliberately to mean "cannot be classified" rather than a new colour.
 * ═══════════════════════════════════════════════════════════════════════════ */

const pct0 = (n) => `${Math.round(Number(n) || 0)}%`;
const centsMoney = (c) => money0((Number(c) || 0) / 100);

/** Rows out of a probe block, or [] — never undefined into a chart. */
function probeRows(block) {
  if (Array.isArray(block)) return block;
  if (block && block.tracked && Array.isArray(block.data)) return block.data;
  return [];
}

/**
 * ── 4 · Open value by rep ──────────────────────────────────────────────────
 * Horizontal bars, sorted by the measure. ⚠ Sorted on a COPY: by_rep is the
 * payload array, shown verbatim in "Figures the AI was given", and sorting it
 * in place would reorder what the reader is checking against.
 */
export function BarByRep({ data, measure = 'open_value', label = 'Open value by rep',
                           onDrill, height = null, compact = false }) {
  const isMoney = measure.endsWith('_value');
  /* ⛔ Card P234 — the right margin the end label is drawn into. */
  const RIGHT = 104;

  const all = useMemo(() => probeRows(data).slice()
    .map((r) => ({
      full: r.rep || 'Unassigned',
      value: Number(r[measure]) || 0,
      opps: Number(r.open_opps) || 0,
      stuck: Number(r.stuck_opps) || 0,
    }))
    .sort((a, b) => b.value - a.value), [data, measure]);

  /*
   * ⛔⛔ Card P228 — the same fold. Daniel's by-rep chart skipped every other
   * tick, so 1047, 1040 and 184 floated beside unlabelled bars.
   */
  const fold = useMemo(() => topNWithRemainder(all, (r) => r.value, TOP_N), [all]);

  /*
   * ⛔⛔ Card P234 — THE AXIS IS SIZED FROM THE NAMES IT HAS TO SHOW. A fixed
   * 96/140 clipped "Roberto Barquero" to "oberto Barquero": truncateLabel
   * budgets two lines and the tick renders one, so a 16-character name was
   * never shortened and never fitted.
   */
  const axisWidth = axisWidthFor(fold.shown.map((r) => r.full),
    { min: compact ? 96 : 140, max: compact ? 150 : 190 });

  /*
   * ⛔⛔ Card P231 — THE AXIS IS SCALED TO THE BARS SHOWN. Previously the
   * folded total was an eleventh bar, so the x-domain was set by "everything
   * else" and the ten rows the reader came for were squeezed into a fraction
   * of the width.
   */
  const maxValue = fold.shown.reduce((m, r) => Math.max(m, r.value), 0);

  const rows = useMemo(() => {
    return fold.shown
      .map((r) => {
        const full = r.full;
        const value = r.value;
        const opps = r.opps;
        return {
          /* ⚠ ONE line, matching what the tick actually renders. */
          rep: truncateLabel(full, axisWidth, 1),
          fullRep: full,
          value,
          opps,
          stuck: r.stuck,
          /*
           * ⛔⛔ CARD P227 C — THE LABEL NOW SAYS WHAT THE BAR SAYS. The bar's
           * LENGTH is the money on the X axis; the end label used to be the
           * opportunity COUNT, so "Manny Razo 180" drew a shorter bar than
           * "Ana Ituarte 134" and read as an arithmetic error. Two numbers
           * for one bar, one of them unlabelled, is a chart arguing with
           * itself. It now carries both, measure first.
           */
          endLabel: (() => {
            /* ⛔ Card P234 — and it must fit the MARGIN on one line, or it
               wraps into the row below and recreates P227's overlap. */
            const text = barEndLabel(value, opps, isMoney ? money0 : String);
            return labelFits(value, maxValue) && labelFitsMargin(text, RIGHT) ? text : '';
          })(),
        };
      });
  }, [fold, isMoney, axisWidth, maxValue]);

  /* ⚠ The remainder as ONE muted line, with the right noun. */
  const caption = foldCaption({
    count: fold.hiddenCount,
    value: fold.hiddenValue,
    opps: fold.hidden.reduce((a, r) => a + (Number(r.opps) || 0), 0),
    noun: 'reps',
    formatValue: isMoney ? money0 : String,
  });

  /*
   * ⛔ Card P227 A — HEIGHT FROM THE DATA. A fixed 220px over thirteen rows
   * is 17px each, and a wrapped two-line label needs 24 — so they printed on
   * top of each other.
   */
  const h = height || barChartHeight(rows.map((r) => r.rep), axisWidth, { min: 180 });
  /* ⛔ A rep with stuck deals is named by a LABEL, not only by a fill — the
     repo's rule: a verdict encoded only in colour is unreadable in the other
     theme and untestable in either. */
  return (
    <Panel testid="chart-bar-by-rep" title={label}
           caveat={rows.length
             ? [`Bars and labels both show ${isMoney ? 'open value' : 'the measure'}; the second number is the deal count.`,
                fold.hiddenCount ? `Top ${TOP_N} by value.` : '',
                'Red marks reps carrying stuck deals.'].filter(Boolean).join(' ')
             : 'No reps in this scope.'}
           footer={caption ? (
             <div data-testid="chart-bar-by-rep-fold"
                  style={{ fontSize: 11, color: C.muted, marginTop: 6, lineHeight: 1.5 }}>
               {caption}
             </div>
           ) : null}
           height={h}>
      {/*
        * ⚠ Card P227 C — THE RIGHT MARGIN HAD TO GROW WITH THE LABEL. The end
        * label used to be a bare count ("134"); it now reads "$345,000 · 134",
        * and at right:44 the longest one was CLIPPED BY THE PLOT EDGE — the
        * regenerated PDF showed "$345,000 · 1". A label the chart cuts in half
        * is worse than the count it replaced.
        */}
      <BarChart data={rows} layout="vertical" margin={{ top: 4, right: RIGHT, left: 0, bottom: 0 }}>
        <CartesianGrid stroke={C.borderSoft} horizontal={false} />
        <XAxis type="number" tick={{ fontSize: 11, fill: C.faint }} axisLine={false}
               tickLine={false} tickFormatter={isMoney ? money0 : undefined} />
        {/* ⛔ Card P228 — interval={0}: every row is named. */}
        <YAxis type="category" dataKey="rep" width={axisWidth} interval={0}
               tick={{ fontSize: 11, fill: C.body }} axisLine={false} tickLine={false} />
        {/* ⚠ The tooltip carries the FULL name, so truncation loses nothing. */}
        <Tooltip formatter={(v) => (isMoney ? money0(v) : v)}
                 labelFormatter={(l, p) => (p && p[0] && p[0].payload.fullRep) || l} />
        <Bar isAnimationActive={false} dataKey="value" radius={[0, 2, 2, 0]}
             onClick={(d) => onDrill && d && onDrill(d.rep)}
             cursor={onDrill ? 'pointer' : 'default'}>
          {rows.map((r) => (
            <Cell key={r.rep} fill={r.stuck > 0 ? C.high : C.primary} />
          ))}
          {/* ⚠ One line, always — see labelFitsMargin. */}
          <LabelList dataKey="endLabel" position="right"
                     style={{ fontSize: 10, fill: C.muted, whiteSpace: 'nowrap' }} />
        </Bar>
      </BarChart>
    </Panel>
  );
}

/**
 * ── 5 · Open value by stage ────────────────────────────────────────────────
 * ⛔⛔ UNMAPPED STAGES ARE SHOWN IN GREY, NOT HIDDEN. A stage with no funnel
 * bucket is still real open value sitting in the pipeline; dropping it from
 * the bar chart would make the stages add up to less than the headline with
 * nothing on screen explaining the gap. Grey says "counted, not classified".
 */
export function BarByStage({ data, label = 'Open value by stage', onDrill,
                             height = null, compact = false }) {
  const RIGHT = 104;

  const all = useMemo(() => probeRows(data).slice()
    .map((r) => {
      const full = r.stage || '(no stage)';
      return {
        full,
        value: Number(r.open_value) || 0,
        opps: Number(r.open_opps) || 0,
        /* ⚠ The payload's own flag, not a guess from the stage name. */
        unmapped: !r.funnel_bucket,
      };
    })
    .sort((a, b) => b.value - a.value), [data]);

  /*
   * ⛔⛔ Card P228 — TOP TEN PLUS "Other (n)". Daniel ran ALL PIPELINES and
   * got ~40 stages in one chart: 1-2px bars, labels in an unreadable block,
   * and recharts auto-skipping most category ticks so the names that showed
   * sat beside bars they did not belong to. Sizing alone cannot fix forty
   * rows; fewer rows can.
   *
   * ⛔⛔ CARD P231 — AND THE REMAINDER IS A CAPTION, NOT AN ELEVENTH BAR. In
   * the real data the tail OUTWEIGHS the top ten (PEM Appt alone carries 566
   * unmapped opps), so an "Other" bar ran the full width and compressed the
   * rows the reader opened the chart to read. The sum is still stated — one
   * muted line below — so nobody mistakes the chart for the whole pipeline.
   */
  const fold = useMemo(() => topNWithRemainder(all, (r) => r.value, TOP_N), [all]);

  /* ⛔ Card P234 — the axis is sized from the stage names it has to show. */
  const axisWidth = axisWidthFor(fold.shown.map((r) => r.full),
    { min: compact ? 110 : 170, max: compact ? 170 : 210 });

  /* ⛔ The x-domain comes from the BARS SHOWN, so they use the full width. */
  const maxValue = fold.shown.reduce((m, r) => Math.max(m, r.value), 0);

  const rows = useMemo(() => fold.shown.map((r) => ({
    stage: truncateLabel(r.full, axisWidth, 1),
    fullStage: r.full,
    value: r.value,
    opps: r.opps,
    unmapped: r.unmapped,
    /* ⛔ Card P227 C — the label says what the bar says. */
    endLabel: (() => {
      const text = barEndLabel(r.value, r.opps, money0);
      return labelFits(r.value, maxValue) && labelFitsMargin(text, RIGHT) ? text : '';
    })(),
  })), [fold, axisWidth, maxValue]);

  const caption = foldCaption({
    count: fold.hiddenCount,
    value: fold.hiddenValue,
    opps: fold.hidden.reduce((a, r) => a + (Number(r.opps) || 0), 0),
    noun: 'stages',
    formatValue: money0,
  });

  /*
   * ⛔⛔ Card P227 A — THIS IS THE CHART DANIEL PRINTED. Thirteen stages with
   * names like "Treatment Recommended & Presented" in a fixed 240px box:
   * 18px a row for labels needing 24, so they overlapped and printed as
   * doubled lettering. The height now comes from the rows and their wrapping.
   */
  const h = height || barChartHeight(rows.map((r) => r.stage), axisWidth, { min: 200 });

  const unmappedCount = rows.filter((r) => r.unmapped).length;

  return (
    <Panel testid="chart-bar-by-stage" title={label}
           caveat={[
             'Bars and labels both show open value; the second number is the deal count.',
             /* ⛔ The fold is STATED. A chart showing ten of forty stages
                without saying so tells the reader the pipeline is smaller
                than it is. */
             fold.hiddenCount ? `Top ${TOP_N} by value.` : '',
             unmappedCount ? `${unmappedCount} stage(s) in grey have no funnel mapping — counted in the totals, not placed in the funnel.` : '',
           ].filter(Boolean).join(' ')}
           footer={caption ? (
             <div data-testid="chart-bar-by-stage-fold"
                  style={{ fontSize: 11, color: C.muted, marginTop: 6, lineHeight: 1.5 }}>
               {caption}
             </div>
           ) : null}
           height={h}>
      {/* ⚠ Card P227 C — room for "$500,000 · 40"; see BarByRep. */}
      <BarChart data={rows} layout="vertical" margin={{ top: 4, right: RIGHT, left: 0, bottom: 0 }}>
        <CartesianGrid stroke={C.borderSoft} horizontal={false} />
        <XAxis type="number" tick={{ fontSize: 11, fill: C.faint }} axisLine={false}
               tickLine={false} tickFormatter={money0} />
        {/* ⛔⛔ Card P228 — interval={0}. recharts AUTO-SKIPS category ticks
            when it thinks they will not fit, which is how names came to sit
            beside bars they did not belong to. Every row is named now, and
            the height computed above is what makes that honest. */}
        <YAxis type="category" dataKey="stage" width={axisWidth} interval={0}
               tick={{ fontSize: 11, fill: C.body }} axisLine={false} tickLine={false} />
        {/* ⚠ The tooltip carries the FULL stage name. */}
        <Tooltip formatter={(v) => money0(v)}
                 labelFormatter={(l, p) => (p && p[0] && p[0].payload.fullStage) || l} />
        <Bar isAnimationActive={false} dataKey="value" radius={[0, 2, 2, 0]}
             onClick={(d) => onDrill && d && onDrill(d.stage)}
             cursor={onDrill ? 'pointer' : 'default'}>
          {/*
            * ⭐ Card P222 item 3 — WCAG 1.4.11 through the BOUNDARY. On a
            * white well a 1px darkened outline of the bar's own hue carries
            * the non-text contrast, so the fill stays the colour it means.
            * Measured against white: primaryDeep 10.36:1, seriesOtherInk
            * 6.35:1 — both well past 3:1.
            *
            * ⚠ The grey is "cannot be classified", not a category — it takes
            * the Other ink for the same reason it takes the Other fill.
            */}
          {rows.map((r) => (
            <Cell key={r.stage}
                  fill={r.unmapped ? C.seriesOther : C.primary}
                  stroke={r.unmapped ? C.seriesOtherInk : C.primaryDeep}
                  strokeWidth={1} />
          ))}
          <LabelList dataKey="endLabel" position="right"
                     style={{ fontSize: 10, fill: C.muted, whiteSpace: 'nowrap' }} />
        </Bar>
      </BarChart>
    </Panel>
  );
}

/**
 * ── 6 · Receivables ageing ─────────────────────────────────────────────────
 *
 * ⛔⛔ THE BUCKETS ARE NOT MINE. 0-7 / 8-30 / 31-90 / 90+ is the definition in
 * routes/_payment_terms.js (AGE_BUCKET_SQL / AGE_LABELS), which the finance
 * screens already use and which Daniel already ruled on. There is a SECOND,
 * different ageing in _finance_drilldown.js that buckets by days PAST DUE
 * rather than days SINCE ISSUE — a different question over overlapping rows.
 * Inventing a third set of boundaries for this chart would have put a third
 * answer in the product; the server hands over the ruled one and this draws it.
 *
 * ⚠ Every bucket renders, including the empty ones — unpaidByAge returns them
 * all on purpose. A bucket omitted because it is zero reads as a bucket that
 * does not exist.
 */
export function ReceivablesAging({ data, label = 'Unpaid invoices by age', height = 200 }) {
  const block = (data && data.tracked && data.data) || (data && data.byBucket ? data : null);
  const rows = useMemo(() => {
    if (!block || !block.byBucket) return [];
    const order = block.order || Object.keys(block.byBucket);
    return order.map((k) => ({
      bucket: (block.byBucket[k] && block.byBucket[k].label) || k,
      key: k,
      owed: Number((block.byBucket[k] || {}).cents) || 0,
      count: Number((block.byBucket[k] || {}).count) || 0,
    }));
  }, [block]);

  const total = rows.reduce((a, r) => a + r.owed, 0);

  return (
    <Panel testid="chart-aging" title={label}
           caveat={block
             ? `${centsMoney(total)} unpaid across ${rows.reduce((a, r) => a + r.count, 0)} invoice(s). Age is measured from ISSUE, not from the due date.`
             : 'Receivables ageing is not tracked for this window.'}
           height={height}>
      <BarChart data={rows} margin={{ top: 6, right: 10, left: 0, bottom: 0 }}>
        <CartesianGrid stroke={C.borderSoft} vertical={false} />
        <XAxis dataKey="bucket" tick={{ fontSize: 11, fill: C.faint }} axisLine={false}
               tickLine={false} interval={0} />
        <YAxis tick={{ fontSize: 11, fill: C.faint }} axisLine={false} tickLine={false}
               width={64} tickFormatter={centsMoney} />
        <Tooltip formatter={(v) => centsMoney(v)} />
        <Bar isAnimationActive={false} dataKey="owed" radius={[2, 2, 0, 0]}>
          {/* ⚠ Older money is worse money — the oldest bucket carries the
              alert token, the rest the neutral one. Both are labelled. */}
          {/*
            * ⭐ Card P222 — outlines carry 1.4.11 on the white well.
            * ⚠ THESE ARE SEMANTIC COLOURS, NOT THE CATEGORICAL RAMP: age is a
            * severity scale, so red/amber keep their meaning and the
            * Okabe-Ito series palette stays out of it — P222 is explicit that
            * the two sets are separate.
            * Measured vs white: highDeep 10.02:1, watchDeep 7.09:1,
            * primaryDeep 10.36:1.
            */}
          {rows.map((r) => (
            <Cell key={r.key}
                  fill={r.key === 'd90_plus' ? C.high
                    : r.key === 'd31_90' ? C.watch : C.primary}
                  stroke={r.key === 'd90_plus' ? C.highDeep
                    : r.key === 'd31_90' ? C.watchDeep : C.primaryDeep}
                  strokeWidth={1} />
          ))}
          <LabelList dataKey="count" position="top" style={{ fontSize: 10, fill: C.muted }} />
        </Bar>
      </BarChart>
    </Panel>
  );
}

/**
 * ── 7 · Lead source mix ────────────────────────────────────────────────────
 * ⚠ referral_source, which routes/_ceo_dashboard.js records as the populated
 * column (99.8%) — NOT lead_source, which is empty. The payload decides that;
 * this only draws it.
 */
const DONUT_FILLS = [C.primary, C.primaryDeep, C.watch, C.high, C.muted, C.faint];

export function SourceDonut({ data, label = 'Lead sources', height = 200, maxSlices = 6 }) {
  const rows = useMemo(() => {
    const src = probeRows(data).slice()
      /* ⚠ `isOther` IS CARRIED THROUGH. Rebuilding the row dropped the flag
         before the sort could see it, so a pre-folded "Other" stayed first
         and kept a categorical colour — found in the evidence PNG, where
         "Other (9)" drew blue at the head of the legend. */
      .map((r) => ({ source: r.source || 'Unknown', count: Number(r.count) || 0,
                     isOther: Boolean(r.isOther) }))
      /* ⚠ Other always sinks, whoever folded it — this component or the
         adapter that fed it. */
      .sort((a, b) => (a.isOther ? 1 : 0) - (b.isOther ? 1 : 0) || b.count - a.count);
    if (src.length <= maxSlices) return src;
    /* ⛔ The tail is AGGREGATED, never truncated. Dropping slices would make
       the ring describe a smaller population than the figure beside it. */
    const head = src.slice(0, maxSlices - 1);
    const rest = src.slice(maxSlices - 1);
    /* ⚠ FLAGGED and appended LAST: "Other" is a residual, not a ranking
       position, so it never outranks a real source even when it is bigger. */
    head.push({ source: `Other (${rest.length})`,
                count: rest.reduce((a, r) => a + r.count, 0), isOther: true });
    return head;
  }, [data, maxSlices]);

  const total = rows.reduce((a, r) => a + r.count, 0);

  return (
    <Panel testid="chart-donut" title={label}
           caveat={total ? `${total} lead(s) with a recorded source in this window.`
                         : 'No leads carry a referral source in this window.'}
           height={height}>
      <PieChart>
        <Pie data={rows} dataKey="count" nameKey="source" innerRadius="48%" outerRadius="78%"
             paddingAngle={1} isAnimationActive={false}>
          {/*
            * ⛔⛔ "Other" IS GREY, AND IT IS LAST — Card P242 B2b / P222.
            *
            * Colours were assigned by POSITION, and because "Other" is a SUM
            * it is usually the largest slice — so it took the first
            * categorical colour while a real source got the grey reserved for
            * unclassified. Measured on a 14-source fixture: "Other (9)" drew
            * blue, first in the legend.
            *
            * ⚠ A reader who has learned that grey means "not a category" is
            * actively misled by that, which is worse than an arbitrary
            * palette — so Other is pinned to SERIES_OTHER and the real
            * sources keep the ramp in their own order.
            */}
          {rows.map((r, i) => (
            <Cell key={r.source}
                  fill={r.isOther ? SERIES_OTHER : DONUT_FILLS[i % DONUT_FILLS.length]}
                  stroke={r.isOther ? SERIES_OTHER_INK : C.rule}
                  strokeWidth={1} />
          ))}
        </Pie>
        {/* ⚠ A LEGEND, because a ring of colours with no names carries no
            information in print or for a colour-blind reader. */}
        <Legend wrapperStyle={{ fontSize: 11, color: C.body }} />
        <Tooltip formatter={(v, n) => [`${v} (${pct0(total ? (v / total) * 100 : 0)})`, n]} />
      </PieChart>
    </Panel>
  );
}

export default {
  RevenueChart, MovementWaterfall, SalesFunnel,
  BarByRep, BarByStage, ReceivablesAging, SourceDonut,
};
